import { useAsyncExecutor2 } from "async-executor-hook";
import { format } from "date-fns";
import { Paginator } from "primereact/paginator";
import { parse, stringify } from "query-string";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  from,
  interval,
  map,
  mapTo,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";

import { Authorization } from "../../../common/AuthorizationCpt";
import { HTTP_METHODS } from "../../../utils/constants";
import { getHHMM } from "../../../utils/dates";
import { networkBackendRequest } from "../../../utils/networkUtils";
import { ITEMS_PER_PAGE } from "../../../utils/valueStaticConstants";
import { SubtitleDiv } from "../../AdminDocumentManagement/AdminDocumentManagementSharedStyles";
import {
  GrayDiv,
  PlateAmount,
  PlateBar,
  PlateDiv,
  PlateSection,
} from "../../AdminTurnConfirmationTable/AdminTurnConfirmationTableStyles";
import ConfirmationModal from "../../ConfirmationModal/ConfirmationModal";
import {
  Row,
  TableBody,
  TableHeader,
} from "../../DocumentManagement/DocumentManagementSharedStyles";
import PaymentLoader from "../../PaymentLoader/PaymentLoader";
import StandaloneMessage from "../../StandaloneMessage/StandaloneMessage";
import StatusTooltip from "../../StatusTooltip/StatusTooltip";
import TurnConfirmationSidebar from "../../TurnConfirmationSidebar/TurnConfirmationSidebar";
import { AdminTurnConfirmationConvertResponse } from "./AdminTurnConfirmationConvertResponse";
import {
  AdminTurnConfirmationParams$,
  AppointmentConvert,
} from "./AdminTurnConfirmation.interface";

const POLLING_INTERVAL = 10000;
const AdminTurnConfirmationTable = (props: AdminTurnConfirmationParams$) => {
  const { params$ } = props;
  const destroy$Ref = useRef(new Subject());
  const destroy$: any = destroy$Ref.current;
  useEffect(() => {
    return () => {
      destroy$.next();
      destroy$.complete();
    };
  }, [destroy$]);

  const loadAdminTurnConfirmation = useAsyncExecutor2(
    (params) => {
      const destroy$ = destroy$Ref.current;
      return interval(POLLING_INTERVAL).pipe(
        takeUntil(destroy$),
        startWith(params),
        mapTo(params),
        switchMap((params) => {
          return interval(POLLING_INTERVAL).pipe(
            takeUntil(destroy$),
            startWith(params),
            mapTo(params),
            switchMap((params) => {
              const request = networkBackendRequest(
                "/appointments/admin-turn/",
                HTTP_METHODS.GET,
                params,
                undefined,
                undefined
              )
                .then((resp) => resp.json())
                .then(AdminTurnConfirmationConvertResponse);
              return from(request);
            })
          );
        })
      );
    },
    {
      params$: params$.pipe(
        takeUntil(destroy$Ref.current),
        map((params: any) => ({
          ...params,
          withdrawal_from_date: format(
            params.withdrawal_from_date,
            "yyyy-MM-dd"
          ),
          withdrawal_to_date: format(params.withdrawal_to_date, "yyyy-MM-dd"),
          order: "descending",
          page: params.page ?? 1,
          page_size: ITEMS_PER_PAGE,
          appointment_status_option: "turn_pending_creation",
        }))
      ),
    }
  );
  const gridTemplateColumns = "repeat(4, 1fr)";
  const history = useHistory();
  const [showSidebar, updateShowSidebar] = useState(false);
  const [sidebarInfo, updateSidebarInfo] = useState<AppointmentConvert>();
  const handleClick = (newSidebarInfo: AppointmentConvert) => {
    updateSidebarInfo(newSidebarInfo);
    updateShowSidebar(true);
  };
  const [messageAdminTurnConfirmation, setMessageAdminTurnConfirmation] =
    useState("");
  const [titleAdminTurnConfirmation, setTitleAdminTurnConfirmation] =
    useState("");
  const [
    showModalSuccessTurnConfirmation,
    setShowModalSuccessTurnConfirmation,
  ] = useState(false);

  const [showModalErrorValidNumTurn, setShowModalErrorValidNumTurn] =
    useState(false);
  const [showMessageErrorValidNumTurn, setShowMessageErrorValidNumTurn] =
    useState("");
  return (
    <div>
      <Authorization actions={["appointment.get_appointment_admin_turn"]}>
        {loadAdminTurnConfirmation.status === "PROCESSING" && (
          <>
            <PaymentLoader numItems={ITEMS_PER_PAGE} />
          </>
        )}

        {loadAdminTurnConfirmation.isSuccess &&
          loadAdminTurnConfirmation.data.data.length > 0 && (
            <TableHeader gridTemplateColumns={gridTemplateColumns}>
              <div className="left-align">ID/AGENTE DE ADUANA</div>
              <div>DESPACHADOR</div>
              <div>PLACAS</div>
              <div>FECHA Y HORA DE RETIRO</div>
            </TableHeader>
          )}
        {loadAdminTurnConfirmation.isSuccess &&
          loadAdminTurnConfirmation.data.data.length === 0 && (
            <StandaloneMessage>
              No se encontraron turnos a confirmar.
            </StandaloneMessage>
          )}
        <TableBody gridTemplateColumns={gridTemplateColumns}>
          {loadAdminTurnConfirmation.isSuccess &&
            loadAdminTurnConfirmation.data.data.map((elem: any) => (
              <Row
                className="clickable-element"
                key={elem.id}
                height={"8.5rem"}
                onClick={() => handleClick(elem)}
              >
                <div className="left-align">
                  <SubtitleDiv>{elem.appointmentNum}</SubtitleDiv>
                  <div>{elem.agentName}</div>
                  <GrayDiv>
                    {elem.volantes.length}{" "}
                    {elem.volantes.length > 1 ? "volantes" : "volante"}
                  </GrayDiv>
                </div>
                <div>{elem.dispatcher}</div>
                <PlateSection>
                  <PlateBar>
                    {elem.withdrawalByHand ? (
                      <PlateDiv className="status" status={"neutral"}>
                        <span>A mano</span>
                      </PlateDiv>
                    ) : (
                      <>
                        <span>
                          {elem.plates[0] && elem.plates[0].toUpperCase()}
                        </span>
                        {elem.plates.length > 1 ? (
                          <PlateAmount status={"neutral"}>
                            <span>{"+" + (elem.plates.length - 1)}</span>
                          </PlateAmount>
                        ) : null}
                      </>
                    )}
                  </PlateBar>
                  {elem.plates.length > 1 ? (
                    <StatusTooltip
                      className="tooltip"
                      statusList={elem.plates
                        .filter((plate) => plate !== elem.plates[0])
                        .map((plate) => {
                          return {
                            category: "neutral",
                            label: plate,
                          };
                        })}
                    />
                  ) : null}
                  {elem.plates.length < 1 && <div>-</div>}
                </PlateSection>
                <div>{`${format(
                  new Date(elem.timeslotStart),
                  "yyyy/MM/dd"
                )} ${getHHMM(elem.timeslotStart)} A ${getHHMM(
                  elem.timeslotEnd
                )}`}</div>
              </Row>
            ))}
        </TableBody>
        {loadAdminTurnConfirmation.status === "SUCCESS" &&
          loadAdminTurnConfirmation.data.data.length > 0 && (
            <div className="flex-paginator">
              <Paginator
                first={
                  (loadAdminTurnConfirmation.data.page - 1) *
                  loadAdminTurnConfirmation.data.page_size
                }
                rows={loadAdminTurnConfirmation.data.page_size}
                totalRecords={loadAdminTurnConfirmation.data.count}
                onPageChange={(e) => {
                  const targetPage =
                    e.first / loadAdminTurnConfirmation.data.page_size;
                  const pathname = history.location.pathname;
                  const parsedParams = parse(history.location.search);
                  const stringifiedParams = stringify({
                    ...parsedParams,
                    page: targetPage + 1,
                  });
                  history.push(`${pathname}?${stringifiedParams}`);
                }}
              />
            </div>
          )}
        <Authorization actions={["appointment.update_appointment_admin"]}>
          {showSidebar && (
            <TurnConfirmationSidebar
              sidebarInfo={sidebarInfo}
              onOutsideClick={() => {
                updateShowSidebar(false);
              }}
              onErrorEndpoint={(data) => {
                setShowModalErrorValidNumTurn(true);
                setShowMessageErrorValidNumTurn(data);
              }}
            />
          )}
        </Authorization>
        {showModalErrorValidNumTurn && showMessageErrorValidNumTurn && (
          <ConfirmationModal
            message={showMessageErrorValidNumTurn}
            onAccept={() => {
              setShowMessageErrorValidNumTurn("");
              setShowModalErrorValidNumTurn(false);
            }}
            title={"Atención"}
            toggleModal={() => {
              setShowMessageErrorValidNumTurn("");
              setShowModalErrorValidNumTurn(false);
            }}
          />
        )}

        {showModalSuccessTurnConfirmation && (
          <ConfirmationModal
            message={messageAdminTurnConfirmation}
            onAccept={() => {
              setMessageAdminTurnConfirmation("");
              setTitleAdminTurnConfirmation("");
              setShowModalSuccessTurnConfirmation(false);
            }}
            title={titleAdminTurnConfirmation}
            toggleModal={() => {
              setMessageAdminTurnConfirmation("");
              setTitleAdminTurnConfirmation("");
              setShowModalSuccessTurnConfirmation(false);
            }}
          />
        )}
      </Authorization>
    </div>
  );
};

export default AdminTurnConfirmationTable;
